import { UIElement } from '../ui-element';
import { element, trustedHTML } from '../../global/template-literals';
import { Labels } from '../../global/labels';
import { removeAttributes, setAttributes } from '../../global/ui-helpers';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import styles from './ui-button.css';
import type { TranslationLabels } from '../../types/labels.type';
import { Timeout } from '../../global/utils.type';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIButton
 * @element ui-button
 * @classdesc Represents a class for <code>ui-button</code> element.
 * Wraps or creates a button according to the brand book.
 * Events (like `onClick`, `onChange` etc) and all generic HTML attributes (`aria-*`, `form*`) are being translated
 * straight to the generated button inside `ui-button` component.
 * In case of icon only button, do not forget to add `aria-label` attribute.
 * @property {string} [label] {@attr label} Button label.
 * @property {UIButtonLayout} [layout] {@attr layout} Button layout.
 * @property {string} glyph {@attr glyph} Icon glyph code.
 * @property {UIButtonIconPosition} iconPosition {@attr icon-position} Icon position (left or right).
 * @property {UIButtonType} [type] {@attr type} Button type.
 * @property {boolean} disabled {@attr disabled} Toggle disabled state of the button.
 * @property {boolean} loader {@attr loader} Toggle loader (spinner icon). Note: while loading, the button should stay enabled for accessibility reasons - do not disable it with <code>disabled</code> attribute. You can however temporarily remove listeners or use any methods that keep the button focusable.
 * @property {HTMLButtonElement} button Read-only. Shortcut to child button element.
 * @property {HTMLElement} icon Read-only. Shortcut to child icon element.
 * @property {HTMLElement} liveRegion Read-only. Shortcut to live region element updated when loader prop changes.
 * @slot
 * @example
 * <ui-button label="Submit" glyph="add" icon-position="left"></ui-button>
 * @example
 * <ui-button aria-label="Add" title="Add" glyph="add"></ui-button>
 * @example
 * <ui-button label="Submit" loader></ui-button>
 */
@Component({
    tag: 'ui-button',
    styles,
})
export class UIButton extends UIElement {
    /**
     * @type {UIButtonLayout}
     * @readonly
     */
    static readonly Layout = {
        Primary: 'primary',
        Secondary: 'secondary',
        Tertiary: 'tertiary',
    };

    /**
     * @type {UIButtonIconPosition}
     * @readonly
     */
    static readonly IconPosition = {
        Left: 'left',
        Right: 'right',
    };

    /**
     * @type {UIButtonType}
     * @readonly
     */
    static readonly Type = {
        Button: 'button',
        Submit: 'submit',
        Reset: 'reset',
    };

    /**
     * Contains initial HTML for the default slot (serving as an alternative to `label` prop)
     * @private
     */
    private slottedHTML!: string;
    private loaderLiveRegionDelay = 1000;
    private liveRegionStartTimeoutId: Timeout | undefined;
    private liveRegionEndTimeoutId: Timeout | undefined;

    /**
     * Attributes to be excluded from the button element.
     * @private
     */
    private excludedButtonAttributes = new Set<string>([
        'label',
        'icon-position',
        'glyph',
        'layout',
        'state',
        'class',
        'loader',
        'id',
    ]);

    @Attribute() label!: string;
    @Attribute() layout: string = UIButton.Layout.Primary;
    @Attribute() glyph!: string;
    @Attribute() iconPosition: string = UIButton.IconPosition.Right;
    @Attribute() type: string = UIButton.Type.Button;
    @Attribute(Boolean) loader = false;
    @Attribute(Boolean) disabled = false;
    @Child(':scope > button') button!: HTMLButtonElement;
    @Child(':scope > button > ui-icon') icon!: HTMLElement;
    @Child('.ui-button__live-region') liveRegion!: HTMLElement;

    /**
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-button', {
            loading: 'Please wait',
        });
    }

    /**
     * @type {string[]}
     */
    static get observedAttributes(): Array<string> {
        return ['disabled', 'loader', 'glyph'];
    }

    /**
     * @inheritDoc
     */
    observeAttributes(name: string, oldValue: string, newValue: string): void {
        if (oldValue === newValue || !this.hydrated) {
            return;
        }

        switch (name) {
            case 'loader':
                this.updateLoadingState();
                // This means loader just got false. We can't rely on oldValue and newValue since they report null
                if (!this.loader) {
                    this.liveRegion.textContent =
                        (this.label ||
                            this.button.getAttribute('aria-label')) ??
                        '';

                    this.clearLiveRegion(true);
                }
                break;
            case 'disabled':
                this.button.disabled = this.disabled;
                break;
            case 'glyph':
                this.glyph = newValue;
                this.icon?.setAttribute('glyph', newValue);
                break;
        }
    }

    /**
     * Passes click when invoked on host to the nested button. Serves as an API sugar allowing programmatic clicks on the host element.
     * @param {MouseEvent} event
     * @private
     */
    handleHostClick = (event: MouseEvent) => {
        if (this.disabled || this.button?.disabled) {
            return;
        }
        if (this.button && event.target === this) {
            this.button.dispatchEvent(
                new MouseEvent('click', { ...event, bubbles: false })
            );
        }
    };

    /**
     * Blocks any click events when disabled.
     * @private
     */
    private blockClicksWhenDisabled = (event: MouseEvent) => {
        if (this.disabled) {
            event.preventDefault();
            event.stopImmediatePropagation();
        }
    };

    /**
     * Clears the timeout for the live region.
     * @param {Timeout} timeoutId
     * @private
     */
    clearLiveRegionTimeout(timeoutId: Timeout | undefined): void {
        clearTimeout(timeoutId);
    }

    /**
     * Clears the live region after some time to remove a virtual cursor stop (eg in VoiceOver)
     * @param {boolean} isEnd - Whether we announce the start of loading or the end
     */
    clearLiveRegion(isEnd: boolean): void {
        if (!isEnd) {
            if (this.liveRegionStartTimeoutId) {
                this.clearLiveRegionTimeout(this.liveRegionStartTimeoutId);
            }
            this.liveRegionStartTimeoutId = setTimeout(() => {
                this.liveRegion.innerHTML = '';
                this.liveRegionStartTimeoutId = undefined;
            }, this.loaderLiveRegionDelay);
        } else {
            if (this.liveRegionEndTimeoutId) {
                this.clearLiveRegionTimeout(this.liveRegionEndTimeoutId);
            }
            this.liveRegionEndTimeoutId = setTimeout(() => {
                this.liveRegion.innerHTML = '';
                this.liveRegionEndTimeoutId = undefined;
            }, this.loaderLiveRegionDelay);
        }
    }

    /**
     * Sets or updates icon element according to the current props.
     * @private
     */
    updateLoadingState(): void {
        if (this.loader) {
            if (!this.icon) {
                const icon = this.createElement({
                    tagName: 'ui-icon',
                    attributes: {
                        glyph: 'loader',
                    },
                });
                this.button.append(icon);
            }
            this.icon?.setAttribute('glyph', 'loader');
            this.liveRegion.textContent = UIButton.labels.loading as string;
            setAttributes(this, {
                'aria-disabled': 'true',
            });

            this.clearLiveRegion(false);
        } else {
            this.liveRegion.innerHTML = '';
            this.removeAttribute('aria-disabled');
            if (!this.glyph) {
                this.icon?.remove();
            } else {
                this.icon?.setAttribute('glyph', this.glyph);
            }
        }
    }

    /**
     * @private
     */
    subscribeEvents() {
        this.addEventListener('click', this.handleHostClick);
        this.addEventListener('click', this.blockClicksWhenDisabled, {
            capture: true,
        });
    }

    /**
     * @private
     */
    unsubscribeEvents() {
        this.removeEventListener('click', this.handleHostClick);
        this.removeEventListener('click', this.blockClicksWhenDisabled, {
            capture: true,
        });
    }

    /**
     * @inheritDoc
     */
    render(): void {
        this.slottedHTML = this.innerHTML.trim();

        const slottedHTML = this.slottedHTML;
        this.innerHTML = '';

        // Get current attributes of the host, excluding attributes from the stop-list
        const genericButtonAttributes = Array.from(this.attributes)
            .filter(
                (attribute: Attr) =>
                    !this.excludedButtonAttributes.has(attribute.name)
            )
            .reduce<Record<string, string>>((collection, attribute: Attr) => {
                collection[attribute.name] = attribute.value;
                return collection;
            }, {});

        if (!this.label && !slottedHTML && this.glyph) {
            this.classList.add('-icon-only');
        }

        const button = element`
            <button type="${this.type}" class="ui-button__button">
                ${
                    this.label &&
                    !slottedHTML &&
                    element`<span class="ui-button__label">${this.label}</span> `
                }
                ${
                    !!slottedHTML &&
                    trustedHTML`
                    <span class="ui-button__label">${slottedHTML}</span>
                `
                }
                ${
                    this.glyph &&
                    element`<ui-icon glyph="${this.glyph}"></ui-icon>`
                }
            </button>
        `;

        const liveRegion = element`
            <div role="status" class="ui-button__live-region"></div>
        `;

        // Set current host attributes (excluding stop-list) on the button
        setAttributes(button, genericButtonAttributes);

        // Remove current attributes from the host (excluding stop-list)
        removeAttributes(
            this,
            Object.keys(genericButtonAttributes).filter(
                (name) => name !== 'disabled'
            )
        );

        this.append(button);
        this.append(liveRegion);
        this.updateLoadingState();
    }

    /**
     * @inheritDoc
     */
    hydrate() {
        this.subscribeEvents();
    }

    /**
     * @inheritDoc
     */
    disconnect() {
        this.unsubscribeEvents();
    }

    /**
     * @inheritDoc
     */
    reconnect() {
        this.subscribeEvents();
    }
}
